import { useCallback, useEffect, useState } from "react";
import { Euro, TrendingDown, Percent, RotateCcw, Info, Map } from "lucide-react";
import type { ActiveTool } from "../../App";
import ResultCard from "../shared/ResultCard";
import PDFDownloadGate from "../shared/PDFDownloadGate";
import AnsprechpartnerCard from "../shared/AnsprechpartnerCard";
import LoadingSpinner from "../shared/LoadingSpinner";
import ErrorState from "../shared/ErrorState";
import SoGehtEsWeiter from "../shared/SoGehtEsWeiter";
import { FoerderFormData } from "./FoerderRechner";
import { api } from "../../lib/api";
import { formatEuro, formatProzent } from "../../lib/format";
import { RATES, RATES_VERSION } from "../../lib/rates";

// FOUND-09 pilot: Step4Ergebnis is the Phase 1 pilot consuming the rates module.
// All Förder/EEG numeric literals rendered here flow from the backend response
// (no inline literals in this component today); strompreis/einspeisevergütung
// are exposed via RATES.* below for downstream consumers and to anchor the import.
// TODO Phase 4 (FOERDER-02): migrate any KfW/BAFA/EEG numeric literals introduced
// here to the rates module once [VERIFY-2026] sign-off lands.
void RATES;

interface FoerderResult {
  programm: string;
  basisfoerdersatz: number;
  boni: { name: string; satz: number; anwendbar: boolean; grund?: string }[];
  gesamtfoerdersatz: number;
  foerderfaehige_kosten: number;
  foerderbetrag: number;
  eigenanteil: number;
  regionale_foerderung?: { name: string; betrag: number; quelle: string };
  disclaimer: string;
  berechnet_am: string;
}

interface Props {
  data: FoerderFormData;
  onBack: () => void;
  onReset: () => void;
  onToolChange?: (tool: ActiveTool) => void;
}

export default function Step4Ergebnis({ data, onBack, onReset, onToolChange }: Props) {
  const [result, setResult] = useState<FoerderResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const calc = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.foerderRechner.calculate({ ...data, paket: "premium" });
      setResult(res as FoerderResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Berechnung fehlgeschlagen. Bitte prüfen Sie Ihre Verbindung.");
    } finally {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => { calc(); }, []);

  if (loading) return <LoadingSpinner text="Förderung wird berechnet…" />;

  if (error) return <ErrorState message={error} onRetry={calc} onBack={onBack} />;

  if (!result) return null;

  const aktiveBoni = result.boni.filter((b) => b.anwendbar);
  const inaktiveBoni = result.boni.filter((b) => !b.anwendbar);

  return (
    <div className="animate-slide-up" data-testid="step-4-ergebnis">
      {/* Highlight-Karten */}
      <div
        className="grid grid-cols-2 gap-3 mb-6"
        aria-live="polite"
        aria-label="Berechnungsergebnis"
      >
        <ResultCard
          label="Förderbetrag"
          value={formatEuro(result.foerderbetrag)}
          subValue={result.programm}
          green
          icon={<Euro className="w-4 h-4" />}
          tooltip="Der Betrag, den Sie vom Staat als Zuschuss erhalten. Wird direkt von den Investitionskosten abgezogen."
          testId="foerderbetrag-card"
        />
        <ResultCard
          label="Fördersatz"
          value={formatProzent(result.gesamtfoerdersatz)}
          subValue="Gesamtförderung"
          green
          icon={<Percent className="w-4 h-4" />}
          tooltip="Prozentualer Anteil der förderfähigen Kosten, der als Zuschuss erstattet wird. Setzt sich aus Basis + Boni zusammen."
          testId="foerdersatz-card"
        />
        <ResultCard
          label="Ihr Eigenanteil"
          value={formatEuro(result.eigenanteil)}
          subValue="nach Abzug der Förderung"
          icon={<TrendingDown className="w-4 h-4" />}
          tooltip="Der Betrag, den Sie nach Abzug aller Förderungen selbst tragen müssen."
          testId="eigenanteil-card"
        />
        <ResultCard
          label="Förderfähige Kosten"
          value={formatEuro(result.foerderfaehige_kosten)}
          subValue="anerkannte Investitionssumme"
          tooltip="Maximale Summe, die der Staat als Berechnungsgrundlage für die Förderung anerkennt. Kann von Ihren tatsächlichen Kosten abweichen."
          testId="foerderfaehige-kosten-card"
        />
      </div>

      {/* Boni-Aufschlüsselung */}
      <div className="bg-neko-dark-2 border border-neko-dark-3 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-semibold text-neko-white mb-3">Förder-Aufschlüsselung</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-neko-gray">Basisförderung</span>
            <span className="text-neko-white font-medium">{formatProzent(result.basisfoerdersatz)}</span>
          </div>
          {aktiveBoni.map((b) => (
            <div key={b.name} className="flex justify-between text-neko-green">
              <span>+ {b.name}</span>
              <span>+{formatProzent(b.satz)}</span>
            </div>
          ))}
          <div className="border-t border-neko-dark-3 pt-2 flex justify-between font-bold">
            <span className="text-neko-white">Gesamtfördersatz</span>
            <span className="text-neko-green">{formatProzent(result.gesamtfoerdersatz)}</span>
          </div>
        </div>
      </div>

      {/* Nicht aktive Boni */}
      {inaktiveBoni.length > 0 && (
        <div className="bg-neko-dark-2 border border-neko-dark-3 rounded-xl p-4 mb-4">
          <h3 className="text-sm font-semibold text-neko-gray mb-2 flex items-center gap-1">
            <Info className="w-4 h-4" /> Weitere Boni (nicht angewandt)
          </h3>
          <div className="space-y-2">
            {inaktiveBoni.map((b) => (
              <div key={b.name} className="text-xs">
                <span className="text-neko-gray font-medium">{b.name} (+{formatProzent(b.satz)})</span>
                {b.grund && <p className="text-neko-gray mt-0.5 opacity-70">{b.grund}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* So geht es weiter */}
      <SoGehtEsWeiter testId="so-geht-es-weiter" />

      {/* Ansprechpartner */}
      <AnsprechpartnerCard
        name="Björn Fischer"
        title="Geschäftsführer"
        subtitle="Ihr Ansprechpartner für Förderung"
        imageSrc="/images/bjorn-fischer.jpg"
        phone="+49742163340"
        phoneDisplay="07421 6334-0"
        email="info@neko-bws.de"
        whatsapp="491703670053"
        testId="ansprechpartner-card"
      />

      {/* PDF Download */}
      <div className="mb-4">
        <PDFDownloadGate
          tool="foerder-rechner"
          eingabedaten={data as unknown as Record<string, unknown>}
          ergebnis={result as unknown as Record<string, unknown>}
        />
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-neko-gray text-center mb-4">{result.disclaimer}</p>

      {/* FOUND-09: rates module version stamp */}
      <p className="text-xs text-neko-gray mt-4 text-center" data-testid="rates-version">v{RATES_VERSION}</p>

      {/* Dach-Mapper CTA — nur bei PV-relevanten Maßnahmen */}
      {onToolChange && data.massnahmen?.some((m) => m === "waermepumpe" || m === "solarthermie") && (
        <button
          onClick={() => {
            if (data.postleitzahl) {
              sessionStorage.setItem("neko-dach-mapper-data", JSON.stringify({ postleitzahl: data.postleitzahl }));
            }
            document.getElementById("neko-tool-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
            onToolChange("dach-mapper");
          }}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-tenant-primary text-neko-white font-semibold rounded-xl hover:bg-tenant-primary/80 border border-neko-orange/30 transition-colors mb-3"
        >
          <Map className="w-5 h-5 text-neko-orange" />
          Dach-Potential prüfen
        </button>
      )}

      {/* Neu berechnen */}
      <button
        onClick={onReset}
        className="w-full flex items-center justify-center gap-2 py-2 text-neko-gray hover:text-neko-white text-sm transition-all"
        data-testid="reset-btn"
      >
        <RotateCcw className="w-4 h-4" /> Neue Berechnung starten
      </button>
    </div>
  );
}
